import { useState, useContext, useRef, useEffect, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Target, History } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const ROWS = 12;
const PEGS_PER_ROW = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
const MULTIPLIERS = [10, 3, 1.5, 1.2, 1, 0.5, 1, 1.2, 1.5, 3, 10];

export const PlinkoPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [isDropping, setIsDropping] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const canvasRef = useRef(null);
  const ballRef = useRef(null);

  const drawBoard = useCallback((ballPos = null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Background
    ctx.fillStyle = '#0F0F10';
    ctx.fillRect(0, 0, width, height);
    
    // Draw pegs
    const pegRadius = 6;
    const startY = 50;
    const rowHeight = (height - 120) / ROWS;
    
    for (let row = 0; row < ROWS; row++) {
      const pegsInRow = PEGS_PER_ROW[row];
      const pegSpacing = width / (pegsInRow + 1);
      
      for (let peg = 0; peg < pegsInRow; peg++) {
        const x = pegSpacing * (peg + 1);
        const y = startY + row * rowHeight;
        
        ctx.beginPath();
        ctx.arc(x, y, pegRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#3B3B3B';
        ctx.fill();
        ctx.strokeStyle = '#5B5B5B';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // Draw multiplier buckets
    const bucketWidth = width / MULTIPLIERS.length;
    const bucketY = height - 50;
    
    MULTIPLIERS.forEach((mult, i) => {
      const x = i * bucketWidth;
      const isHighValue = mult >= 3;
      const isMidValue = mult >= 1.2 && mult < 3;
      
      ctx.fillStyle = isHighValue ? 'rgba(0, 231, 1, 0.3)' : isMidValue ? 'rgba(255, 214, 0, 0.2)' : 'rgba(255, 59, 48, 0.2)';
      ctx.fillRect(x + 2, bucketY, bucketWidth - 4, 40);
      
      ctx.fillStyle = isHighValue ? '#00E701' : isMidValue ? '#FFD600' : '#FF3B30';
      ctx.font = 'bold 12px JetBrains Mono';
      ctx.textAlign = 'center';
      ctx.fillText(`${mult}x`, x + bucketWidth / 2, bucketY + 25);
    });
    
    // Draw ball
    if (ballPos) {
      ctx.beginPath();
      ctx.arc(ballPos.x, ballPos.y, 10, 0, Math.PI * 2);
      ctx.fillStyle = '#FFD600';
      ctx.fill();
      ctx.strokeStyle = '#FF6B00';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, []);

  useEffect(() => {
    drawBoard();
  }, [drawBoard]);

  const dropBall = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0) || isDropping) {
      toast.error("Invalid bet amount");
      return;
    }

    setIsDropping(true);
    setLastResult(null);

    try {
      // Deduct bet
      await axios.post(`${API}/games/dice/play`, {
        game_type: "plinko",
        bet_amount: betAmount,
        bet_data: {}
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      await refreshBalance();

      // Animate ball
      const canvas = canvasRef.current;
      const width = canvas.width;
      const height = canvas.height;
      
      let x = width / 2 + (Math.random() - 0.5) * 30;
      let y = 20;
      let vx = 0;
      let vy = 0;
      
      const gravity = 0.3;
      const bounce = 0.7;
      const friction = 0.99;
      
      const startY = 50;
      const rowHeight = (height - 120) / ROWS;
      
      const animate = () => {
        vy += gravity;
        x += vx;
        y += vy;
        vx *= friction;
        
        // Check peg collisions
        for (let row = 0; row < ROWS; row++) {
          const pegsInRow = PEGS_PER_ROW[row];
          const pegSpacing = width / (pegsInRow + 1);
          const pegY = startY + row * rowHeight;
          
          for (let peg = 0; peg < pegsInRow; peg++) {
            const pegX = pegSpacing * (peg + 1);
            const dx = x - pegX;
            const dy = y - pegY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist < 16) {
              const angle = Math.atan2(dy, dx);
              x = pegX + Math.cos(angle) * 16;
              y = pegY + Math.sin(angle) * 16;
              
              const speed = Math.sqrt(vx * vx + vy * vy);
              vx = Math.cos(angle) * speed * bounce + (Math.random() - 0.5) * 2;
              vy = Math.sin(angle) * speed * bounce;
            }
          }
        }
        
        // Walls
        if (x < 10) { x = 10; vx = -vx * bounce; }
        if (x > width - 10) { x = width - 10; vx = -vx * bounce; }
        
        drawBoard({ x, y });
        
        // Check if reached bottom
        if (y >= height - 60) {
          const bucketIndex = Math.min(Math.floor(x / (width / MULTIPLIERS.length)), MULTIPLIERS.length - 1);
          const mult = MULTIPLIERS[bucketIndex];
          const winnings = betAmount * mult;
          
          setLastResult({ multiplier: mult, winnings, won: mult >= 1 });
          setHistory(prev => [{ mult, winnings, won: mult >= 1 }, ...prev.slice(0, 9)]);
          
          if (mult >= 1) {
            // Add winnings
            axios.post(`${API}/wallet/deposit`, {
              amount: winnings,
              transaction_type: "win"
            }, {
              withCredentials: true,
              headers: { Authorization: `Bearer ${token}` }
            }).then(() => refreshBalance());
            
            toast.success(`${mult}x! Won ₹${winnings.toFixed(2)}`);
          } else {
            toast.error(`${mult}x - Lost ₹${(betAmount - winnings).toFixed(2)}`);
          }
          
          setIsDropping(false);
          return;
        }
        
        ballRef.current = requestAnimationFrame(animate);
      };
      
      ballRef.current = requestAnimationFrame(animate);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to play");
      setIsDropping(false);
    }
  };

  useEffect(() => {
    return () => {
      if (ballRef.current) {
        cancelAnimationFrame(ballRef.current);
      }
    };
  }, []);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center">
            <Target className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="plinko-title">Plinko</h1>
            <p className="text-[#A1A1AA] text-sm">Drop the ball and win big!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] overflow-hidden">
              <canvas
                ref={canvasRef}
                width={500}
                height={500}
                className="w-full h-auto"
                data-testid="plinko-canvas"
              />
              
              {/* Result Display */}
              {lastResult && (
                <div className={`p-4 text-center ${lastResult.won ? 'bg-[#00E701]/20' : 'bg-[#FF3B30]/20'}`}>
                  <span className={`text-2xl font-bold font-mono ${lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                    {lastResult.multiplier}x - {lastResult.won ? '+' : ''}₹{lastResult.winnings.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Controls */}
              <div className="p-6 border-t border-white/[0.08]">
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={isDropping}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono"
                      data-testid="plinko-bet-input"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    {[50, 100, 500].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        disabled={isDropping}
                        className="border-white/10 text-white/60 hover:text-white"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={dropBall}
                  disabled={isDropping || betAmount <= 0 || betAmount > (user?.balance || 0)}
                  className="w-full h-14 bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold text-lg"
                  data-testid="plinko-drop-btn"
                >
                  {isDropping ? 'Dropping...' : 'DROP BALL'}
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">Multipliers</h3>
              <div className="grid grid-cols-5 gap-1 text-center text-xs">
                {MULTIPLIERS.map((mult, i) => (
                  <div 
                    key={i}
                    className={`p-2 rounded ${
                      mult >= 3 ? 'bg-[#00E701]/20 text-[#00E701]' :
                      mult >= 1 ? 'bg-[#FFD600]/20 text-[#FFD600]' :
                      'bg-[#FF3B30]/20 text-[#FF3B30]'
                    }`}
                  >
                    {mult}x
                  </div>
                ))}
              </div>
            </div>

            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">History</h3>
              </div>
              <div className="space-y-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div key={idx} className={`flex justify-between p-2 rounded-lg ${h.won ? 'bg-[#00E701]/10' : 'bg-[#FF3B30]/10'}`}>
                    <span className={`font-mono ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.mult}x
                    </span>
                    <span className={`font-mono ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.won ? '+' : ''}₹{h.winnings.toFixed(0)}
                    </span>
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm text-center">No drops yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
